import { Avatar, Carousel, Col, Rate, Row, Spin } from 'antd'
import React, { useState, useRef, useEffect, useMemo } from 'react'
import suv from '../../Assets/images/suv.webp'
import supercar from '../../Assets/images/supercar.webp'
import bmw from '../../Assets/images/bmw.webp'
import { TiTick } from "react-icons/ti";
import BasicButton from '../../Components/basicButton'
import './index.css'
import ContentWithBgLayout from '../../Components/contentWithBgLayout'
import cityBg from '../../Assets/images/cityBg.webp'
import jamaicaImg from '../../Assets/images/cities/Jamaica.webp'
import DestinationCard from '../../Components/destinationCard'
import { GrNext } from "react-icons/gr";
import { GrPrevious } from "react-icons/gr";
import carousalDivider from '../../Config/utils/CarousalDivider'
import useResponsiveCarouselChunkSize, { gridColsClassForChunk } from '../../Config/hooks/useResponsiveCarouselChunkSize'
import VehicleCard from '../../Components/vehicleCard'
import homeAlaska from '../../Assets/images/homeAlaska.webp'
import appDownload from '../../Assets/images/appDownload.webp'
import { IoLocationSharp } from "react-icons/io5";
import { IoMdHeart } from "react-icons/io";
import { IoIosShareAlt } from "react-icons/io";
import { FaApple } from "react-icons/fa";
import { GrAndroid } from "react-icons/gr";
import { FaUserAlt } from "react-icons/fa";
import NewsLetter from '../../Components/newsLetter'
import useVehicle from '../../Config/hooks/Apis/useVehicle'
import useTour from '../../Config/hooks/Apis/useTour'
import { getVehicleImageUrl } from '../../Config/utils/vehicleMediaUtils'
import { getCityLocalImage, mapTourCardsToUi } from '../../Config/utils/tourMediaUtils'
import { isTourEnabled } from '../../Config/utils/featureFlags'
import useGeneralStore from '../../Store/generalStore'
import SeoHead from '../../Components/seoHead'
import { getSeoPage } from '../../Config/seo'

// Helper functions to extract vehicle details from attributes
const extractSeatCount = (attributes) => {
    if (!attributes || attributes.length === 0) return '2';
    const seatAttr = attributes.find(attr => attr.toLowerCase().includes('seats'));
    if (seatAttr) {
        const match = seatAttr.match(/(\d+)\s*seats?/i);
        if (match) return match[1];
    }
    return '2'; // Default
};

const extractTransmission = (attributes) => {
    if (!attributes || attributes.length === 0) return 'Manual';
    const autoAttr = attributes.find(attr => 
        attr.toLowerCase().includes('automatic') || 
        attr.toLowerCase().includes('auto')
    );
    return autoAttr ? 'Automatic' : 'Manual';
};

const extractLitre = (attributes) => {
    if (!attributes || attributes.length === 0) return '90';
    // Look for any numeric value that might represent fuel capacity
    // Default to 90 if not found
    return '90'; // Default value
};

const reviews = [
    {
        comment: "Booked a SUV for our Jacksonville trip in minutes on the website. Pickup was smooth, the car was clean, and returning it at the end of our vacation was hassle-free. Will definitely rent from Classique again.",
        user: <FaUserAlt />,
        name: 'Marcus Thompson',
        rating: 5
    },
    {
        comment: "We used Classique for our Jamaica vacation and couldn't be happier. The online booking was straightforward, prices were clear with no surprises, and having a reliable car made exploring the island so much easier.",
        user: <FaUserAlt />,
        name: 'Sandra Williams',
        rating: 5
    },
    {
        comment: "Rented a car in Orlando for a family theme-park trip. The site showed real availability, confirmation came right away, and the vehicle was exactly what we reserved. Great experience from start to finish.",
        user: <FaUserAlt />,
        name: 'David Chen',
        rating: 5
    },
    {
        comment: "I booked through Classique for a week in Montego Bay. Customer support answered my questions before the trip, and the rental desk had everything ready when we landed. Highly recommend for Caribbean travel.",
        user: <FaUserAlt />,
        name: 'Patricia Brown',
        rating: 5
    },
    {
        comment: "We've rented with Classique in Puerto Rico twice now. Both times the website made comparing vehicles and adding insurance simple, and the cars were in excellent condition. Our go-to for island getaways.",
        user: <FaUserAlt />,
        name: 'James Rodriguez',
        rating: 5
    },
    {
        comment: "First time using Classique for a Trinidad road trip. Easy to search by dates, secure checkout, and a friendly pickup process. Having our own car let us see places we would have missed on tours alone.",
        user: <FaUserAlt />,
        name: 'Michelle Clarke',
        rating: 5
    }
]

const Home = () => {

    const [destinations, setDestinations] = useState([]);
    const [activeCity, setActiveCity] = useState(null);
    const [featuredTours, setFeaturedTours] = useState([]);
    const popularDestinationRef = useRef(null);
    const carRef = useRef(null);
    const reviewsRef = useRef(null);
    const { getFeaturedVehicles } = useVehicle();
    const { getFeatured: getFeaturedTours, getDestinations } = useTour();
    const [featuredVehicles, setFeaturedVehicles] = useState([]);
    const [loadingFeatured, setLoadingFeatured] = useState(false);
    const [loadingFeaturedTours, setLoadingFeaturedTours] = useState(false);
    const tourEnabled = isTourEnabled();
    const cardsPerSlide = useResponsiveCarouselChunkSize(4);
    const reviewsPerSlide = useResponsiveCarouselChunkSize(3);

    const popularDestinations = useMemo(
        () => carousalDivider(featuredTours, cardsPerSlide),
        [featuredTours, cardsPerSlide]
    );
    const reviewsData = useMemo(
        () => carousalDivider(reviews, reviewsPerSlide),
        [reviewsPerSlide]
    );

    // Fetch featured vehicles on component mount
    useEffect(() => {
        const fetchFeatured = async () => {
            try {
                setLoadingFeatured(true);
                const vehicles = await getFeaturedVehicles();
                
                // Transform API data to match VehicleCard format
                const transformedVehicles = vehicles.map(vehicle => ({
                    image: getVehicleImageUrl(vehicle, cityBg),
                    title: vehicle.name || 'Vehicle',
                    desc: vehicle.type || 'Vehicle Type',
                    price: vehicle.baseRentPerDay || 0,
                    type: extractTransmission(vehicle.attributes),
                    count: extractSeatCount(vehicle.attributes),
                    litre: extractLitre(vehicle.attributes)
                }));
                
                setFeaturedVehicles(transformedVehicles);
            } catch (error) {
                console.error('Failed to fetch featured vehicles:', error);
                // Keep empty array on error
                setFeaturedVehicles([]);
            } finally {
                setLoadingFeatured(false);
            }
        };

        fetchFeatured();
    }, []);

    useEffect(() => {
        if (!tourEnabled) return;

        const loadTours = async () => {
            try {
                setLoadingFeaturedTours(true);
                const [dests, featured] = await Promise.all([
                    getDestinations(),
                    getFeaturedTours(),
                ]);
                const normalizedDests = (dests || []).map((d) => ({
                    id: d.name ?? d.Name,
                    name: d.name ?? d.Name,
                    tourCount: d.tourCount ?? d.TourCount ?? 0,
                }));
                setDestinations(normalizedDests);
                if (normalizedDests.length > 0) {
                    setActiveCity(normalizedDests[0]);
                }
                setFeaturedTours(mapTourCardsToUi(featured));
            } catch (error) {
                console.error('Failed to fetch featured destinations:', error);
                setFeaturedTours([]);
            } finally {
                setLoadingFeaturedTours(false);
            }
        };

        loadTours();
    }, [tourEnabled]);

    const cars = useMemo(
        () => carousalDivider(featuredVehicles, cardsPerSlide),
        [featuredVehicles, cardsPerSlide]
    );
    const activeCityImage = getCityLocalImage(activeCity?.name, jamaicaImg);
    const slideGridClass = gridColsClassForChunk(cardsPerSlide);
    const reviewsGridClass = gridColsClassForChunk(reviewsPerSlide);

    const requestFocusHeroPickup = useGeneralStore((state) => state.requestFocusHeroPickup);
    const requestFocusHeroDestination = useGeneralStore((state) => state.requestFocusHeroDestination);
    const setInfo = useGeneralStore((state) => state.setInfo);

    // Scroll to top function for Rent Now button
    const handleRentNow = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleGetStarted = () => {
        requestFocusHeroPickup();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleBookNow = () => {
        requestFocusHeroDestination();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleAppComingSoon = () => {
        setInfo('Our mobile app is coming soon. Stay tuned!');
    };

    const [activeCarousalBtn, setActiveCarousalBtn] = useState({
        destinations: { prev: false, next: true },
        cars: { prev: false, next: true },
        reviews: { prev: false, next: true }
    })

    useEffect(() => {
        popularDestinationRef.current?.goTo?.(0, false);
        carRef.current?.goTo?.(0, false);
        reviewsRef.current?.goTo?.(0, false);
        setActiveCarousalBtn((previous) => ({
            ...previous,
            destinations: { prev: false, next: true },
            cars: { prev: false, next: true },
            reviews: { prev: false, next: true },
        }));
    }, [cardsPerSlide, reviewsPerSlide]);

    const citySelectHandler = (city) => {
        setActiveCity(city)
    }

    const carousal1SlideChangeHandler = (event) => {
        console.log(event)
    }

    const previousHandler = (name) => {
        if (name == 'destinations') {
            popularDestinationRef.current?.prev?.();
            setActiveCarousalBtn((previous) => ({ ...previous, destinations: { prev: true, next: false } }))
        }
        else if (name == 'reviews') {
            reviewsRef.current?.prev?.();
            setActiveCarousalBtn((previous) => ({ ...previous, reviews: { prev: true, next: false } }))
        }
        else {
            carRef.current?.prev?.();
            setActiveCarousalBtn((previous) => ({ ...previous, cars: { prev: true, next: false } }))
        }
    }

    const nextHandler = (name) => {
        if (name == 'destinations') {
            popularDestinationRef.current?.next?.();
            setActiveCarousalBtn((previous) => ({ ...previous, destinations: { prev: false, next: true } }))
        }
        else if (name == 'reviews') {
            reviewsRef.current?.next?.();
            setActiveCarousalBtn((previous) => ({ ...previous, reviews: { prev: false, next: true } }))
        }
        else {
            carRef.current?.next?.();
            setActiveCarousalBtn((previous) => ({ ...previous, cars: { prev: false, next: true } }))
        }
    }


    const homeSeo = getSeoPage('home');
    return (
        <>
            <SeoHead
                title={homeSeo?.title}
                description={homeSeo?.description}
                canonicalPath="/"
            />
        <Row className='flex flex-col gap-4 mt-16 sm:mt-20'>
            <Row className='gap-2 md:gap-4 lg:gap-8 max-w-[1100px] w-full px-8 mx-auto my-5 h-fit'>
                <Col lg={14} md={15} sm={24} xs={24} className='flex my-2 '>
                    <Row className='w-full flex flex-row h-full gap-4 justify-center items-center sm:justify-start sm:items-center'>
                        <Col className='flex mx-auto sm:mx-0 sm:flex-col gap-4 h-fit justify-center items-center md:items-start'>
                            <div className='h-full sm:h-[200px] w-full'>
                                <img src={suv} alt='SUV rental car' loading='lazy' decoding='async' className='w-full h-full object-cover object-center' />
                            </div>
                            <div className='h-full sm:h-[200px] w-full lg:w-[180px] self-end'>
                                <img src={supercar} alt='Luxury supercar rental' loading='lazy' decoding='async' className='w-full h-full object-cover object-center' />
                            </div>
                        </Col>

                        <Col className='flex gap-2 items-start sm:mt-6 lg:mt-0 lg:items-center mx-auto sm:mx-0'>
                            <div className='h-[200px] sm:h-[260px]'>
                                <img src={bmw} alt='BMW rental car' loading='lazy' decoding='async' className='w-full h-full object-cover object-center' />
                            </div>
                        </Col>
                    </Row>
                </Col>
                <Col lg={9} md={8} sm={24} xs={24} className=' flex flex-col py-6 text-left gap-4'>
                    <h3 className='main-heading text-3xl font-bold'>Get A Fair Price On Your Rental Car, Tours & Transfers Today</h3>
                    <p className='text-[#778088]'>We are committed to providing our customers with exceptional service, competitive pricing, and a wide range of units.</p>
                    <div className='flex flex-col gap-3 text-[#778088]'>
                        <div className='flex gap-2 items-start'>
                            <div className='p-1 bg-[#405FF212] rounded-full'>
                                <TiTick />
                            </div>
                            <span>Top notch independent car rental service.</span>
                        </div>
                        <div className='flex gap-2 items-start'>
                            <div className='p-1 bg-[#405FF212] rounded-full'>
                                <TiTick />
                            </div>
                            <span>Extensive fleet of cars to choose from.</span>
                        </div>
                        <div className='flex gap-2 items-start'>
                            <div className='p-1 bg-[#405FF212] rounded-full'>
                                <TiTick />
                            </div>
                            <span>Excellent customer satisfaction.</span>
                        </div>
                    </div>
                    <div className='mt-2'>
                        <BasicButton text='Get Started' basicButtonHandler={handleGetStarted} className='get-started-cta' />
                    </div>
                </Col>
            </Row>

            {tourEnabled && <Row className='gap-2 md:gap-4 max-w-[1100px] w-full px-8 mx-auto my-5 h-fit flex items-center justify-center'>
                <Col span={24} className='flex flex-col items-center justify-center text-center'>
                    <h3 className='main-heading text-2xl font-bold'>Explore Popular Cities</h3>
                    <p className='text-[#778088]'>Browse top destination, find the perfect rental car and things to do for your trip.</p>
                </Col>

                <Col span={24} className='w-full max-w-[1000px] my-3'>
                    <div className='cities-chip-scroll w-full overflow-x-auto flex gap-4 items-center justify-start scroll-snap-x scroll-snap-mandatory'>
                        {
                            destinations.map((city) => {
                                return (
                                    <p key={city.id} className={`cities-chip py-2 px-4 text-nowrap border border-[#2ED1E1] w-fit font-semibold shrink-0 scroll-snap-align-start ${activeCity?.id === city.id ? 'btn-linear-gradient text-white border-none' : 'text-black'} cursor-pointer`} onClick={() => citySelectHandler(city)}>
                                        {city.name}
                                    </p>
                                )
                            })
                        }
                    </div>
                </Col>

                <Col lg={24}>
                    <div className='w-full relative'>
                        <img src={activeCityImage} alt='Popular city travel destination' loading='lazy' className='w-full h-[400px] object-cover' />
                        <Row className='h-[100px]'>
                            <Row className='w-[90%] h-[200px] bg-[#F9FDFF] city-detail-card mx-auto z-20 absolute top-[330px] right-5 left-5 py-5 px-4 overflow-y-scroll flex justify-between items-center'>
                                <Col lg={8}>
                                    <h2 className='main-heading text-3xl font-bold'>{activeCity?.name || 'Destinations'}</h2>
                                    <p className='text-[#778088]'>
                                        {activeCity
                                            ? `Explore ${activeCity.tourCount} experience${activeCity.tourCount === 1 ? '' : 's'} in ${activeCity.name}.`
                                            : 'Browse top destinations and things to do for your trip.'}
                                    </p>
                                </Col>
                                <Col lg={10} className='flex flex-col gap-2 mt-2'>
                                    <div className='w-full'>
                                        <p className='px-2 py-3 city-detail-tag w-fit text-[#D176E0] font-semibold'>Affordable Packages</p>
                                    </div>
                                    <div className='w-full flex justify-between gap-2 flex-wrap sm:flex-nowrap'>
                                        <div className='w-full sm:w-1/2'>
                                            <p className='px-2 py-3 city-detail-tag w-fit text-[#D176E0] font-semibold break-all'>Accommodations</p>
                                        </div>
                                        <div className='w-full sm:w-1/2'>
                                            <p className='px-2 py-3 city-detail-tag w-fit text-[#D176E0] font-semibold'>Airport Pickup</p>
                                        </div>
                                    </div>
                                    <div className='w-full flex justify-between gap-2 flex-wrap sm:flex-nowrap'>
                                        <div className='w-full sm:w-1/2'>
                                            <p className='px-2 py-3 city-detail-tag w-fit text-[#D176E0] font-semibold'>Trusted Local Guides</p>
                                        </div>
                                        <div className='w-full sm:w-1/2'>
                                            <p className='px-2 py-3 city-detail-tag w-fit text-[#D176E0] font-semibold'>24/7 Travel Support</p>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Row>
                    </div>
                </Col>
            </Row>}
            {/*
            <Row className='gap-2 md:gap-4 max-w-[1100px] w-full px-8 mx-auto my-5 h-fit grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4'>
                {
                    data && data.map((item) => {
                        return (
                            <Col>
                                <DestinationCard cardData={item} />
                            </Col>
                        )
                    })
                }
            </Row>
            */}

            {tourEnabled && <Row className='w-full trending-bg py-8 gap-4 sm:gap-0 px-2 sm:px-0' justify={'space-around'} align={'middle'}>
                <Col lg={12} md={12} sm={12} xs={24} className='flex items-center justify-center'>
                    <img src={homeAlaska} alt='Wellness tourism destination' loading='lazy' className='h-80 w-80' />
                </Col>
                <Col lg={12} md={12} sm={12} xs={24} className='flex flex-col gap-2 items-center sm:items-start'>
                    <p className='bg-[#AFFFF0] px-3 py-2 w-fit font-semibold'>Trending Now</p>
                    <h3 className='main-heading text-2xl font-semibold text-white'>Wellness Tourism</h3>
                    <div className='flex gap-2 items-start flex-wrap justify-center'>
                        <div className='text-white flex gap-1 items-center justify-center'>
                            <IoLocationSharp />
                            <span>Jacksonville, Florida</span>
                        </div>
                        <div className=' text-white'> | </div>
                        <div className='flex items-center gap-2 text-white'>
                            <Rate disabled value={4.5} allowHalf />
                            <span>4.5</span>
                            <span>( 300 reviews )</span>
                        </div>
                    </div>
                    <div className='max-w-[400px] my-3'>
                        <p className='text-white text-center sm:text-start'>
                            Jacksonville is known for its warm climate, beautiful beaches, and vibrant culture. It’s a great place to visit for a vacation.
                        </p>
                    </div>
                    <div className='flex items-center gap-4'>
                        <BasicButton text='Book Now' basicButtonHandler={handleBookNow} />
                        <div className=' text-white'> | </div>
                        <div className='flex items-center gap-1'>
                            <span className='p-2 flex items-center justify-center bg-[#76a9b6] rounded-full '>
                                <IoMdHeart color='white' />
                            </span>
                            <span className='p-2 flex items-center justify-center bg-[#76a9b6] rounded-full '>
                                <IoIosShareAlt color='white' />
                            </span>
                        </div>
                    </div>
                </Col>
            </Row>}

            {/* Featured Destinations */}
            {tourEnabled && <Row className='gap-2 md:gap-4 max-w-[1100px] w-full px-8 mx-auto my-5 h-fit'>
                <Row className='w-full' justify={'space-between'} align={'middle'}>
                    <Col lg={10} md={16} sm={16} xs={18} className=''>
                        <h3 className='main-heading text-2xl font-bold'>Featured Destinations</h3>
                        <p className='text-[#778088]'>Explore our most popular destinations, hand picked for unforgettable experiences.</p>
                    </Col>
                    <Col lg={10} md={8} sm={8} xs={6} className=' flex gap-3 justify-end'>
                        <div className={`flex items-center justify-center w-7 cursor-pointer h-7 ${activeCarousalBtn.destinations.prev ? 'bg-[#FFDA32]' : 'border border-[#FFDA32]'} rounded-full`} onClick={() => previousHandler('destinations')}>
                            <GrPrevious />
                        </div>
                        <div className={`flex items-center justify-center w-7 h-7 cursor-pointer ${activeCarousalBtn.destinations.next ? 'bg-[#FFDA32]' : 'border border-[#FFDA32]'} rounded-full`} onClick={() => nextHandler('destinations')}>
                            <GrNext />
                        </div>
                    </Col>
                </Row>

                <div className='w-full'>
                    {loadingFeaturedTours ? (
                        <div className='flex justify-center items-center py-20'>
                            <Spin size="large" />
                        </div>
                    ) : popularDestinations && popularDestinations.length > 0 ? (
                        <Carousel
                            swipe={false}
                            afterChange={carousal1SlideChangeHandler}
                            dots={false}
                            draggable={false}
                            ref={popularDestinationRef}
                            className='w-full'
                        >
                            {
                                popularDestinations.map((destination, index) => {
                                    return (
                                        <div className="w-full" key={index}>
                                            <Row className={`w-full grid ${slideGridClass} gap-2 items-start`} justify={'space-between'} align={'middle'}>
                                                {
                                                    destination?.map((dest) => {
                                                        return (
                                                            <Col key={dest.id}>
                                                                <DestinationCard cardData={dest} />
                                                            </Col>
                                                        )
                                                    })
                                                }
                                            </Row>
                                        </div>
                                    )
                                })
                            }
                        </Carousel>
                    ) : (
                        <div className='text-center py-20'>
                            <p className='text-lg text-gray-500'>No featured destinations available.</p>
                        </div>
                    )}
                </div>
            </Row>}

            {/* explore all vehicle */}
            <Row className='gap-2 md:gap-4 max-w-[1100px] w-full px-8 mx-auto my-5 h-fit'>
                <Row className='w-full' justify={'space-between'} align={'middle'}>
                    <Col lg={10} md={16} sm={16} xs={18}>
                        <h3 className='main-heading text-2xl font-bold'>Explore All Vehicles</h3>
                        <p className='text-[#778088]'>Discover our premium fleet of rental vehicles. Browse through our wide selection of cars, from economy to luxury, and find the perfect ride for your journey.</p>
                    </Col>
                    <Col lg={10} md={8} sm={8} xs={6} className=' flex gap-3 justify-end'>
                        <div className={`flex items-center justify-center w-7 cursor-pointer h-7 ${activeCarousalBtn.cars.prev ? 'bg-[#FFDA32]' : 'border border-[#FFDA32]'} rounded-full`} onClick={() => previousHandler('cars')}>
                            <GrPrevious />
                        </div>
                        <div className={`flex items-center justify-center w-7 h-7 cursor-pointer ${activeCarousalBtn.cars.next ? 'bg-[#FFDA32]' : 'border border-[#FFDA32]'} rounded-full`} onClick={() => nextHandler('cars')}>
                            <GrNext />
                        </div>
                    </Col>
                </Row>

                <div className='w-full'>
                    {loadingFeatured ? (
                        <div className='flex justify-center items-center py-20'>
                            <Spin size="large" />
                        </div>
                    ) : featuredVehicles.length > 0 ? (
                        <Carousel
                            swipe={false}
                            afterChange={carousal1SlideChangeHandler}
                            dots={false}
                            draggable={false}
                            ref={carRef}
                            className='w-full'
                        >
                            {
                                cars && cars.map((car, index) => {
                                    return (
                                        <div className="w-full" key={index}>
                                            <Row className={`w-full grid ${slideGridClass} gap-2 lg:gap-3 items-start`} justify={'space-between'} align={'middle'}>
                                                {
                                                    car?.map((carDetail, carIndex) => {
                                                        return (
                                                            <Col key={carIndex}>
                                                                <VehicleCard cardData={carDetail} onRentNow={handleRentNow} />
                                                            </Col>
                                                        )
                                                    })
                                                }
                                            </Row>
                                        </div>
                                    )
                                })
                            }
                        </Carousel>
                    ) : (
                        <div className='text-center py-20'>
                            <p className='text-lg text-gray-500'>No featured vehicles available at the moment.</p>
                        </div>
                    )}
                </div>
            </Row>

            {/* app download */}
            <Row className='w-full appDownload-bg py-8' justify={'space-around'} align={'middle'}>
                <Col lg={12} md={12} sm={24} xs={24} className='flex items-center justify-center'>
                    <img src={appDownload} alt='Download the Classique mobile app' loading='lazy' className='h-80 w-80' />
                </Col>
                <Col lg={12} md={12} sm={24} xs={24} className='flex flex-col gap-4 text-white items-center md:items-start md:gap-0 px-2 md:px-0'>
                    <h3 className='main-heading text-2xl font-bold mt-4 md:mt-0'>Classique Mobile App</h3>
                    <p className='font-semibold'>Available on IOS & Android</p>
                    <div className='max-w-[400px] my-3'>
                        <p className='text-center md:text-start'>
                            Book your rental car anytime, anywhere. The Classique app lets you browse our fleet, compare prices, manage your reservations, and get support on the go—so your perfect ride is always just a tap away.
                        </p>
                    </div>
                    <div className='flex items-center gap-3 md:flex-wrap px-3 md:px-0'>
                        <button
                            type='button'
                            onClick={handleAppComingSoon}
                            className='flex gap-2 items-center py-3 px-4 bg-[#FFDA32] text-black cursor-pointer border-0'
                        >
                            <FaApple size={20} />
                            <span className='font-semibold'>Download For IOS</span>
                        </button>
                        <button
                            type='button'
                            onClick={handleAppComingSoon}
                            className='flex gap-2 items-center py-3 px-4 bg-[#FFDA32] text-black cursor-pointer border-0'
                        >
                            <GrAndroid size={20} />
                            <span className='font-semibold'>Download For Android</span>
                        </button>
                    </div>
                </Col>
            </Row>

            {/* reviews */}
            <Row className='max-w-[1100px] w-full mx-auto my-6 px-2 md:px-0' justify={'center'} align={'middle'}>
                <Col span={24} className='flex items-center justify-center flex-col'>
                    <p className='main-heading text-2xl md:text-3xl font-bold'>Contact us to review</p>
                    <p className='main-heading text-2xl md:text-3xl font-bold'> <span className='text-[#2ED1E1]'>your experience</span> with us</p>
                    <p className='max-w-[350px] text-center text-[#778088] my-3'>Give us feedback and let us know what experiences you had while on vacation with us</p>
                </Col>

                <div className='w-full mt-4'>
                    <div className='flex items-center mx-auto justify-between'>
                        <div className={`flex items-center mx-1 lg:mx-0 justify-center w-7 cursor-pointer h-7 ${activeCarousalBtn.reviews.prev ? 'bg-[#FFDA32]' : 'border border-[#FFDA32]'} rounded-full`} onClick={() => previousHandler('reviews')}>
                            <GrPrevious />
                        </div>

                        <Carousel
                            swipe={false}
                            afterChange={carousal1SlideChangeHandler}
                            dots={false}
                            draggable={false}
                            ref={reviewsRef}
                            className='max-w-[250px] sm:max-w-[500px] md:max-w-[700px] lg:max-w-[900px] w-full mx-auto'
                        >
                            {
                                reviewsData && reviewsData.map((review, index) => {
                                    return (
                                        <div className="w-full mx-auto" key={index}>
                                            <Row className={`w-full gap-2 lg:gap-3 grid ${reviewsGridClass} items-center`} justify={'space-between'} align={'middle'}>
                                                {
                                                    review?.map((reviewRecord) => {
                                                        return (
                                                            <Col key={reviewRecord.name} className='flex flex-col py-5 px-3 gap-2 reviews-card'>
                                                                <p className='text-[#12121299] text-sm'>{reviewRecord.comment}</p>
                                                                <div className='flex items-center gap-3'>
                                                                    <Avatar icon={reviewRecord.user} shape='circle' />
                                                                    <div className='flex flex-col gap-2 items-start'>
                                                                        <p className='text-xs'>{reviewRecord.name}</p>
                                                                        <Rate value={reviewRecord.rating} disabled allowHalf />
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                        )
                                                    })
                                                }

                                            </Row>
                                        </div>
                                    )
                                })
                            }

                        </Carousel>

                        <div className={`flex items-center  mx-1 lg:mx-0 justify-center w-7 h-7 cursor-pointer ${activeCarousalBtn.reviews.next ? 'bg-[#FFDA32]' : 'border border-[#FFDA32]'} rounded-full`} onClick={() => nextHandler('reviews')}>
                            <GrNext />
                        </div>
                    </div>
                </div>
            </Row>

            <Row className='w-full mx-auto flex items-center justify-center'>
                <NewsLetter
                    className='w-full flex items-center flex-col justify-center gap-3 news-letter-bg py-12 px-2'
                />
            </Row>
        </Row>
        </>
    )
}

export default Home